import type { INewProductUI } from "../+page.server";
import { FixtureProductManager, type IFilterOption } from "./product-manager";
import { cartAdd, afterCartClearFnDel, afterCartRemoveFnDel } from "$lib/store/cart-context";

export class FixtureManager {
  public products = new FixtureProductManager(this.eventuuid, `${this.subcategorySlug}${this.slug}`);

  // DOM
  public fixtureContainer: HTMLElement | undefined;

  constructor(
    private eventuuid: string,
    private subcategorySlug: string,
    private slug: string,
    products: INewProductUI[]
  ) {
    this.onSvgLoaded = this.onSvgLoaded.bind(this);
    this.sortDirection = this.sortDirection.bind(this);
    this.onSvgClick = this.onSvgClick.bind(this);
    this.init(products);
  }

  /**
  rProds = which service products to refresh
  */
  public init(products: INewProductUI[]) {
    this.products.init(products);
  }

  public async refresh(rProds: string[] = [], cacheName: string) {
    // refresh all tickets or specific services
    await this.products.refresh(rProds, cacheName);
  }

  // return the list of products
  public get displayList() {
    return this.products.displayList;
  }

  public set displayList(v: any) {
    this.products.displayList = v;
  }

  // Filter
  // return the list of categories of the products
  public get getAvailableCategories() {
    return this.products.getAvailableCategories;
  }

  public get getAvailableBlocks() {
    return this.products.getAvailableBlocks;
  }

  public get getAvailableTicketFormat() {
    return this.products.getAvailableTicketFormat;
  }

  public runCategoriesFilter(args: IFilterOption) {
    // Just for colours
    this.svgMap?.handleSectionFilter(this.fixtureContainer, args?.category);
    this.products.runFilter(args);
  }

  public runFilter(args: IFilterOption) {
    this.products.runFilter(args);
  }

  public sortDirection(value: string) {
    this.products.sortOptionFn(value);
  }

  public cleanFilter() {
    this.svgMap?.cleanMap(this.fixtureContainer);
    this.products.runFilter({ reset: true });
  }

  // Filter end
  public async onDestroy() {
    afterCartClearFnDel("fixture-refresh");
    afterCartRemoveFnDel("fixture-refresh");
  }

  public async addToCart(productId: string, userQty: number) {
    return await cartAdd(productId, userQty);
  }

  // SVG
  private svgMap?: ISvgMapFunctions;
  public onSvgLoaded(fns: ISvgMapFunctions) {
    this.svgMap = fns;
  }

  private reset = false;
  public onSvgClick(ev: Event, filterArgs: IFilterOption): { block?: string; category?: string[] } {
    ev.stopImmediatePropagation();

    // This handle onclick for the map
    this.svgMap?.onClick(ev, this.getAvailableBlocks, this.fixtureContainer);

    // This handle products list
    let sectionEle = ev.target as HTMLElement;
    const block = (sectionEle?.dataset?.block || "").toLowerCase();
    const selCategory = sectionEle.dataset.section || "";
    if (!block && !selCategory) {
      return { block: "", category: [] };
    }

    const category = [selCategory.toLowerCase()];
    const tags = sectionEle.dataset.tags || "";
    tags
      .split(",")
      .filter(v => v)
      .map(v => category.push(v.toLowerCase()));
    const prodBlock = this.getAvailableBlocks.has(block);
    // If block
    if (prodBlock) {
      if (this.reset) {
        this.runFilter({ ...filterArgs, block: "", category: [] });
      } else {
        this.runFilter({ ...filterArgs, block, category: [] });
      }
    } else if (this.reset) {
      this.runFilter({ ...filterArgs, block: "", category: [] });
    } else {
      this.runFilter({ ...filterArgs, category, block: "" });
    }

    this.reset = !this.reset;
    return { block: prodBlock ? block : "", category };
  }
}

// SVG Interfaces
export interface ISvgMapFunctions {
  // unsetPath: (cats: Set<string>, alt: Set<string>, blocks: Set<string>) => void;
  onClick: (ev: Event, blocks: Set<string>, fixtureContainer: HTMLElement | undefined) => void;
  handleSectionFilter: (fixtureContainer: HTMLElement | undefined, categories?: string | string[]) => void;
  cleanMap: (fixtureContainer: HTMLElement | undefined) => void;
}
